import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardMedia, Typography, Box, Link } from '@mui/material';
import { FaTwitch } from 'react-icons/fa';
import { ControlledImage, DEFAULT_IMAGE_SETTINGS } from './ImageControls';

const UpcomingEventCard = ({ event, pulseDateTime }) => {
  const [imageUrl, setImageUrl] = useState('');

  const normalizeImageSource = (rawSource) => {
    if (!rawSource) return '';

    const source = Array.isArray(rawSource)
      ? rawSource[0]
      : typeof rawSource === 'object' && rawSource !== null
        ? rawSource.url || rawSource.path || ''
        : rawSource;

    if (!source) return '';

    const trimmed = String(source).trim();

    if (!trimmed) return '';

    // Skip database image IDs - only use local paths
    if (/^\d+$/.test(trimmed)) {
      return '';
    }

    // Skip API image paths - only use local paths
    if (trimmed.startsWith('/api/images/')) {
      return '';
    }

    // Allow external URLs (https://)
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    // Local path - ensure it starts with /
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  };

  // Handle image loading errors by trying lowercase extension if uppercase fails and vice versa
  const handleImageError = () => {
    const currentUrl = imageUrl || '';
    console.log('UpcomingEventCard - Image load error for:', currentUrl);
    
    if (!currentUrl) return;

    // Local/public images: try case variants for common extensions
    // If the current URL ends with uppercase extension, try lowercase
    if (currentUrl.endsWith('.JPG')) {
      setImageUrl(currentUrl.replace('.JPG', '.jpg'));
    }
    // If the current URL ends with lowercase extension, try uppercase
    else if (currentUrl.endsWith('.jpg')) {
      setImageUrl(currentUrl.replace('.jpg', '.JPG'));
    }
    // Similar for PNG/png
    else if (currentUrl.endsWith('.PNG')) {
      setImageUrl(currentUrl.replace('.PNG', '.png'));
    }
    else if (currentUrl.endsWith('.png')) {
      setImageUrl(currentUrl.replace('.png', '.PNG'));
    }
  };

  // Normalize the provided event.image_url to handle DB image ids and legacy paths
  useEffect(() => {
    const normalized = normalizeImageSource(event?.image_url);
    console.log('UpcomingEventCard - Normalized image URL:', normalized);
    setImageUrl(normalized);
  }, [event?.image_url]);

  const eventDateTime = event.upcoming_dates.slice(0, 1).map((date, index) => {
    const eventDate = new Date(date.event_start);
    const eventEndDate = new Date(date.event_end);
    
    const formattedDate = eventDate.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    const formattedStartTime = eventDate.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const formattedEndTime = eventEndDate.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    const format = date.description || '';

    return (
      <Box 
        key={index}
        className="event-date-container"
      >
        <Typography 
          variant="body1"
          color="text.primary"
          align="center"
          className={`event-date-text ${pulseDateTime ? 'pulse' : ''}`}
        >
          {formattedDate}
        </Typography>
        <Typography 
          variant="body2"
          color="text.secondary"
          align="center"
          className={`event-time-text ${pulseDateTime ? 'pulse' : ''}`}
        >
          {`${formattedStartTime} - ${formattedEndTime}`}
        </Typography>
      </Box>
    );
  });

  return (
    <Box
      className="event-card-container"
    >
      {imageUrl && (
        <Box className="event-image-container">
          <ControlledImage
            src={imageUrl}
            alt={event.event_name}
            settings={event.image_settings || DEFAULT_IMAGE_SETTINGS}
            onError={handleImageError}
            className="event-image-media"
          />
        </Box>
      )}
      <CardContent>
        <Typography variant="h5" component="div" className="event-card-title" align="center">
          {event.event_name === 'DJ Coffee Sessions' ? `${event.event_name} ${event.upcoming_dates[0].event_id} - ${event.upcoming_dates[0].description}` : event.event_name}
        </Typography>

        <Typography
          variant="body1"
          color="text.primary"
          align="center"
          className={`event-card-artists ${pulseDateTime ? 'pulse' : ''}`}
        >
          {event.upcoming_dates[0].featured_artist_names.join(' • ')}
        </Typography>

        {/* DateTime */}
        <Box className="event-datetime-container">
          {eventDateTime}
        </Box>

        {/* Location */}
        {event.location && (
          <Box className="event-location-container">
            <Link
              href={event.location.website}
              target="_blank"
              rel="noopener noreferrer"
              className="event-location-link"
            >
              <Typography variant="body1" color="text.primary" align="center">
                {event.location.venue}
              </Typography>
            </Link>
            <Typography variant="body2" color="text.secondary" align="center">
              {event.location.address}
            </Typography>
          </Box>
        )}

        {/* Ticket Information */}
        {event.ticket_info && (
          <Box className="event-ticket-container">
            <Typography variant="body1" color="text.primary" align="center">
              {event.ticket_info.price}
            </Typography>
            {event.ticket_info.ticket_link && (
              <Link
                href={event.ticket_info.ticket_link}
                target="_blank"
                rel="noopener noreferrer"
                className="ticket-button"
              >
                <Typography className="ticket-text">
                  Buy Tickets
                </Typography>
              </Link>
            )}
          </Box>
        )}

        {/* Action Links */}
        <Box className="event-actions-container">
          {event.livestream_link && (
            <Link
              href={event.livestream_link}
              target="_blank"
              rel="noopener noreferrer"
              className="livestream-button"
            >
              <FaTwitch size={24} />
              <Typography className="livestream-text">
                Watch Live
              </Typography>
            </Link>
          )}
        </Box>
      </CardContent>
    </Box>
  );
};

export default UpcomingEventCard;
